import { Apartment, KeyboardArrowDown, LocationOn, MailOutline, Phone } from "@mui/icons-material";
import { DocumentationAgency } from "../../data/types";
import { ARLINGTON_AGENCIES, emptyAgency } from "../../utils/documentationSteps";

type DocumentationAgencyFieldsProps = {
  agency?: DocumentationAgency
  agencies?: DocumentationAgency[]
  onChange: (agency: DocumentationAgency) => void
  showContactFields?: boolean
  placeholder?: string
};

/** Agency picker with optional address, email, and phone fields. */
const DocumentationAgencyFields = ({
  agency,
  agencies = ARLINGTON_AGENCIES,
  onChange,
  showContactFields = true,
  placeholder = "Select agency involved in this task",
}: DocumentationAgencyFieldsProps) => {
  const options = [
    { name: "", address: "", email: "", phone: "" },
    ...agencies,
  ];
  const selectedName = agency?.name || "";

  const handleSelect = (name: string) => {
    if (!name) {
      onChange(emptyAgency());
      return;
    }
    const match = agencies.find((item) => item.name === name);
    onChange(match ? { ...match } : { ...(agency || emptyAgency()), name });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <Apartment className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-primary-400" style={{ height: 18, width: 18 }} />
        <select
          aria-label="Agency"
          className="w-full appearance-none rounded-lg border border-[#E9E9E9] bg-white py-3 pl-10 pr-10 font-raleway text-[14px] text-black"
          value={selectedName}
          onChange={(event) => handleSelect(event.target.value)}
        >
          <option value="">{placeholder}</option>
          {options.filter((item) => item.name).map((item) => (
            <option key={item.name} value={item.name}>
              {item.name}
            </option>
          ))}
        </select>
        <KeyboardArrowDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#808080]" style={{ height: 18, width: 18 }} />
      </div>
      {selectedName && agency?.address ? (
        <p className="px-1 text-[12px] font-open-sans text-[#808080]">{agency.address}</p>
      ) : null}
      {showContactFields ? (
        <>
          <label className="relative">
            <LocationOn className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#808080]" style={{ height: 16, width: 16 }} />
            <input
              aria-label="Agency address"
              className="w-full rounded-lg border border-[#E9E9E9] py-2.5 pl-10 pr-3 font-open-sans text-[14px]"
              placeholder="Enter agency address"
              value={agency?.address || ""}
              onChange={(event) => onChange({ ...(agency || emptyAgency()), address: event.target.value })}
            />
          </label>
          <label className="relative">
            <MailOutline className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#808080]" style={{ height: 16, width: 16 }} />
            <input
              aria-label="Agency email"
              className="w-full rounded-lg border border-[#E9E9E9] py-2.5 pl-10 pr-3 font-open-sans text-[14px]"
              placeholder="Enter agency mail"
              value={agency?.email || ""}
              onChange={(event) => onChange({ ...(agency || emptyAgency()), email: event.target.value })}
            />
          </label>
          <label className="relative">
            <Phone className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#808080]" style={{ height: 16, width: 16 }} />
            <input
              aria-label="Agency phone"
              className="w-full rounded-lg border border-[#E9E9E9] py-2.5 pl-10 pr-3 font-open-sans text-[14px]"
              placeholder="Enter agency phone number"
              value={agency?.phone || ""}
              onChange={(event) => onChange({ ...(agency || emptyAgency()), phone: event.target.value })}
            />
          </label>
        </>
      ) : null}
    </div>
  );
};

export default DocumentationAgencyFields;
